import { BpCard } from '../../../../shared/components/cards/card/BpCard'
import { useTranslation } from 'react-i18next'
import { BpCheckbox } from '../../../../shared/components/forms/checkbox/BpCheckbox'
import { BpButton } from '../../../../shared/components/buttons/bp-button/BpButton'
import { BpTooltip } from '../../../../shared/components/cards/tooltip/BpTooltip'
import { IconPencil } from '../../../../shared/components/icons/IconPencil'
import { Tooltip } from 'antd'
import { useAppDispatch } from '../../../../app/store/hooks/Redux'
import { showAlert } from '../../../../app/store/reducers/AppSlice'
import { openAlertingSchedule } from '../../../../lib/widgets/alerting-schedule-modal/AlertingScheduleModal'
import { useParams } from 'react-router-dom'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import {
  ProjectNotificationsCard,
  useGetNotificationStateQuery,
  useLazyGetProjectDownloadReportQuery,
  usePostNotificationSaveCardMutation,
  usePostProjectSendAlertEmailMutation,
  usePostProjectSendAlertTelegramMutation,
  usePostProjectSendTmAlertEmailMutation,
  usePostProjectSendTmAlertTelegramMutation,
  usePostProjectSendWeeklyReportEmailMutation,
  usePostProjectSendWeeklyReportTelegramMutation,
} from '../../../../app/api'
import {
  ALERT_TYPES,
  AlertType,
  RecipientChannel,
  scheduleLabel,
  scheduleTooltip,
} from '../../../../lib/widgets/alerting-schedule-modal/model/alerting'
import { useTimezones } from '../../../../lib/widgets/alerting-schedule-modal/model/useTimezones'
import { RecipientField } from '../../../../lib/widgets/alerting-schedule-modal/ui/RecipientField'
import { RecipientChips } from '../../../../lib/widgets/alerting-schedule-modal/ui/RecipientChips'
import { FormControls } from '../../../../shared/components/forms/form-controls/FormControls'
import { AiButton } from '../../../../shared/components/ai/ai-button/AiButton'
import { useAiChat } from '../../../../shared/components/ai/ai-provider/AiProvider'
import { Preloader } from '../../../../shared/components/cards/preloader/Preloader'
import { Field } from '../../../../shared/components/forms/utility/field/Field'
import { handleCommonResponse } from '../../../../shared/lib/helpers/ErrorHandlingHelplers'

interface ICardRecipient {
  channel: RecipientChannel
  value: string
  subscriptions: AlertType[]
  /** Добавлен в карточке: подписывается ещё и на типы, включённые на момент сохранения */
  addedInCard?: boolean
}

interface ICardDraft {
  types: Record<AlertType, boolean>
  weekly_report: boolean
  /** Последний добавленный — первым */
  recipients: ICardRecipient[]
}

const fromServer = (state: ProjectNotificationsCard): ICardDraft => ({
  types: {
    affiliates: state.types.affiliates.enabled,
    tm_infringements: state.types.tm_infringements.enabled,
  },
  weekly_report: state.weekly_report,
  recipients: [...state.recipients].reverse(),
})

const subscriptionsOf = (recipient: ICardRecipient, types: ICardDraft['types']): AlertType[] =>
  recipient.addedInCard
    ? ALERT_TYPES.filter((type) => types[type] || recipient.subscriptions.includes(type))
    : recipient.subscriptions

/** Список на сохранение: получатель без единого типа остаётся — ему уходит weekly report */
const toPayloadRecipients = ({ recipients, types }: ICardDraft) =>
  recipients
    .map((recipient) => ({
      channel: recipient.channel,
      value: recipient.value,
      subscriptions: subscriptionsOf(recipient, types),
    }))
    .reverse()

const TYPE_ROWS: { type: AlertType; label: string; tooltip: string }[] = [
  {
    type: 'affiliates',
    label: 'components.alertingSchedule.types.affiliates',
    tooltip: 'pages.project.notifyAboutNewAffiliatesTooltip',
  },
  {
    type: 'tm_infringements',
    label: 'components.alertingSchedule.types.tmShort',
    tooltip: 'components.alertingSchedule.tmTooltip',
  },
]

const same = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b)

export const ProjectNotifications = () => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const { id } = useParams()
  const projectId = Number(id)
  const { handleSendMessage } = useAiChat()
  const timezones = useTimezones()

  const { data, isFetching } = useGetNotificationStateQuery({ projectId }, { skip: !projectId })
  const [saveCard] = usePostNotificationSaveCardMutation()
  const [downloadReport, { isFetching: downloadReportLoading }] =
    useLazyGetProjectDownloadReportQuery()
  const [sendWeeklyReportTelegram, { isLoading: sendWeeklyReportTelegramLoading }] =
    usePostProjectSendWeeklyReportTelegramMutation()
  const [sendWeeklyReportEmail, { isLoading: sendWeeklyReportEmailLoading }] =
    usePostProjectSendWeeklyReportEmailMutation()
  const [sendAlertTelegram, { isLoading: sendAlertTelegramLoading }] =
    usePostProjectSendAlertTelegramMutation()
  const [sendAlertEmail, { isLoading: sendAlertEmailLoading }] =
    usePostProjectSendAlertEmailMutation()
  const [sendTmAlertTelegram, { isLoading: sendTmAlertTelegramLoading }] =
    usePostProjectSendTmAlertTelegramMutation()
  const [sendTmAlertEmail, { isLoading: sendTmAlertEmailLoading }] =
    usePostProjectSendTmAlertEmailMutation()

  const state = data?.data
  const saved = useMemo(() => (state ? fromServer(state) : undefined), [state])
  const [draft, setDraft] = useState<ICardDraft>()
  // Отправленное и принятое бэком состояние до прихода перезапроса: без него Save changes между
  // ответом мутации и обновлённым state на миг снова становилась активной
  const [pendingSaved, setPendingSaved] = useState<ICardDraft>()
  const [submitting, setSubmitting] = useState(false)
  const previousSaved = useRef<ICardDraft>()
  const resetOnSave = useRef(false)

  // Новое сохранённое состояние — своё сохранение или модалка расписания, включившая тип, —
  // ложится в черновик. Поля, которые юзер успел поменять, остаются его правками
  useEffect(() => {
    if (!saved) return

    const previous = previousSaved.current
    const reset = resetOnSave.current

    previousSaved.current = saved
    resetOnSave.current = false
    setPendingSaved(undefined)

    setDraft((current) => {
      if (!current || !previous || reset) return saved

      const keep = <T,>(draftValue: T, previousValue: T, savedValue: T): T =>
        same(draftValue, previousValue) ? savedValue : draftValue

      return {
        types: {
          affiliates: keep(
            current.types.affiliates,
            previous.types.affiliates,
            saved.types.affiliates,
          ),
          tm_infringements: keep(
            current.types.tm_infringements,
            previous.types.tm_infringements,
            saved.types.tm_infringements,
          ),
        },
        weekly_report: keep(current.weekly_report, previous.weekly_report, saved.weekly_report),
        recipients: keep(current.recipients, previous.recipients, saved.recipients),
      }
    })
  }, [saved])

  const baseline = pendingSaved ?? saved
  const dirty = !!draft && !!baseline && !same(draft, baseline)

  const update = (patch: Partial<ICardDraft>) => draft && setDraft({ ...draft, ...patch })

  // повторное добавление не плодит чип: существующий поднимается наверх и дополняет типы подписки
  const addRecipient = (channel: RecipientChannel, value: string) => {
    if (!draft) return

    const existing = draft.recipients.find(
      (recipient) => recipient.channel === channel && recipient.value === value,
    )

    update({
      recipients: [
        { channel, value, subscriptions: existing?.subscriptions ?? [], addedInCard: true },
        ...draft.recipients.filter((recipient) => recipient !== existing),
      ],
    })
  }

  const removeRecipient = (channel: RecipientChannel, value: string) =>
    draft &&
    update({
      recipients: draft.recipients.filter(
        (recipient) => recipient.channel !== channel || recipient.value !== value,
      ),
    })

  const submit = async () => {
    if (!draft) return

    setSubmitting(true)

    const response = await saveCard({
      notificationCardForm: {
        project_id: projectId,
        types: draft.types,
        weekly_report: draft.weekly_report,
        recipients: toPayloadRecipients(draft),
      },
    })
      .unwrap()
      .catch(() => ({ success: false, errors: { save: [t('notifications.failedToSave')] } }))

    if (response.success) {
      const accepted: ICardDraft = {
        types: draft.types,
        weekly_report: draft.weekly_report,
        recipients: toPayloadRecipients(draft).reverse(),
      }

      resetOnSave.current = true
      setPendingSaved(accepted)
      setDraft(accepted)
    } else {
      handleCommonResponse(response, dispatch)
    }

    setSubmitting(false)
  }

  const sendTest = (request: Promise<{ success?: boolean }>) =>
    request
      .then((response) => response.success !== false)
      .catch(() => false)
      .then((success) =>
        dispatch(
          showAlert({
            message: t(success ? 'notifications.successfullySent' : 'notifications.failedToSend'),
            timeout: 3000,
            type: success ? 'success' : 'error',
          }),
        ),
      )

  // тестовые отправки уходят сохранённым получателям, поэтому и доступность — по сохранённым
  const hasSavedRecipients = (channel: RecipientChannel) =>
    !!state?.recipients.some((recipient) => recipient.channel === channel)

  if (!draft || !state) {
    return <div className={'relative h-[320px]'}>{isFetching && <Preloader />}</div>
  }

  const channels: {
    channel: RecipientChannel
    action?: JSX.Element
    sendReport: () => void
    sendReportLoading: boolean
    sendAlert: () => void
    sendAlertLoading: boolean
    sendTmAlert: () => void
    sendTmAlertLoading: boolean
  }[] = [
    {
      channel: 'email',
      sendReport: () => sendTest(sendWeeklyReportEmail({ projectId }).unwrap()),
      sendReportLoading: sendWeeklyReportEmailLoading,
      sendAlert: () => sendTest(sendAlertEmail({ projectId }).unwrap()),
      sendAlertLoading: sendAlertEmailLoading,
      sendTmAlert: () => sendTest(sendTmAlertEmail({ projectId }).unwrap()),
      sendTmAlertLoading: sendTmAlertEmailLoading,
    },
    {
      channel: 'tg',
      action: (
        <AiButton
          unstyled
          size={'sm'}
          onClick={() =>
            handleSendMessage({
              message:
                'Please provide a step-by-step guide on how to set up automated report delivery to a Telegram chat',
            })
          }
        >
          {t('pages.project.howToSetUp')}
        </AiButton>
      ),
      sendReport: () => sendTest(sendWeeklyReportTelegram({ projectId }).unwrap()),
      sendReportLoading: sendWeeklyReportTelegramLoading,
      sendAlert: () => sendTest(sendAlertTelegram({ projectId }).unwrap()),
      sendAlertLoading: sendAlertTelegramLoading,
      sendTmAlert: () => sendTest(sendTmAlertTelegram({ projectId }).unwrap()),
      sendTmAlertLoading: sendTmAlertTelegramLoading,
    },
  ]

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        submit()
      }}
    >
      <BpCard
        footer={
          <FormControls
            canceled={dirty && !submitting}
            disabled={!dirty || submitting}
            onCancel={() => baseline && setDraft(baseline)}
          />
        }
        description={t('pages.project.notificationsDescription')}
        size={'sm'}
        title={t('pages.project.notifications')}
      >
        <div className={'flex flex-col gap-[24px]'}>
          <div>
            {TYPE_ROWS.map(({ type, label, tooltip }) => (
              <div
                key={type}
                className={
                  'flex flex-wrap items-center justify-between gap-x-[16px] gap-y-[4px] border-b border-b-[#00326D]/[.08] py-[12px]'
                }
              >
                <div className={'flex items-center gap-[8px]'}>
                  <BpCheckbox
                    checked={draft.types[type]}
                    label={t(label)}
                    onChange={(e) =>
                      update({ types: { ...draft.types, [type]: e.target.checked } })
                    }
                  />
                  <BpTooltip title={t(tooltip)} />
                </div>
                <Tooltip
                  title={scheduleTooltip(t, state.types[type], timezones.label).map((line) => (
                    <div key={line}>{line}</div>
                  ))}
                  placement={'topRight'}
                >
                  <button
                    className={
                      'flex items-center gap-[6px] whitespace-nowrap text-[14px] leading-[16px] text-focus hover:text-focus-500'
                    }
                    type={'button'}
                    onClick={() => dispatch(openAlertingSchedule(projectId, type))}
                  >
                    <span>{scheduleLabel(t, state.types[type])}</span>
                    <span className={'w-[12px]'}>
                      <IconPencil color={'#0e63d8'} />
                    </span>
                  </button>
                </Tooltip>
              </div>
            ))}
            <div
              className={
                'flex flex-wrap items-center justify-between gap-x-[16px] gap-y-[4px] py-[12px]'
              }
            >
              <div className={'flex items-center gap-[8px]'}>
                <BpCheckbox
                  checked={draft.weekly_report}
                  label={t('pages.project.sendFullWeeklyReport')}
                  onChange={(e) => update({ weekly_report: e.target.checked })}
                />
                <BpTooltip title={t('pages.project.sendFullWeeklyReportTooltip')} />
              </div>
              <span className={'text-[14px] leading-[16px] text-[#6684A7]'}>
                {t('components.alertingSchedule.everyMonday')}
              </span>
            </div>
          </div>
          <div>
            <BpButton
              className={'w-fit'}
              loading={downloadReportLoading}
              size={'sm'}
              theme={'outline'}
              type={'button'}
              onClick={() => downloadReport({ projectId })}
            >
              {t('pages.project.download7DayReport')}
            </BpButton>
          </div>
          <div className={'flex flex-col gap-[16px]'}>
            <div className={'flex flex-col gap-[8px]'}>
              <h2 className={'text-[20px]'}>{t('components.alertingSchedule.alertRecipients')}</h2>
              <p className={'text-[13px] text-[#8E7CA7]'}>
                {t('components.alertingSchedule.alertRecipientsDescription')}
              </p>
            </div>
            <div className={'flex flex-col gap-[8px]'}>
              {channels.map(
                ({
                  channel,
                  action,
                  sendReport,
                  sendReportLoading,
                  sendAlert,
                  sendAlertLoading,
                  sendTmAlert,
                  sendTmAlertLoading,
                }) => (
                  <div
                    key={channel}
                    className={
                      'flex flex-col gap-[8px] rounded-[8px] bg-focus/[.03] py-[12px] px-[16px]'
                    }
                  >
                    <RecipientField
                      action={action}
                      channel={channel}
                      onAdd={(value) => addRecipient(channel, value)}
                    />
                    <RecipientChips
                      items={draft.recipients
                        .filter((recipient) => recipient.channel === channel)
                        .map((recipient) => ({
                          value: recipient.value,
                          tags: subscriptionsOf(recipient, draft.types),
                        }))}
                      removeTooltip={t('components.alertingSchedule.removeRecipient')}
                      onRemove={(value) => removeRecipient(channel, value)}
                    />
                    <div className={'mt-[8px] border-t border-t-[#00326D]/[.08] pt-[16px]'}>
                      <Field fieldSize={'sm'} label={t('pages.project.testSend')} theme={'text'}>
                        <div className={'flex flex-wrap gap-[8px]'}>
                          <BpButton
                            className={'w-fit'}
                            disabled={!hasSavedRecipients(channel)}
                            loading={sendReportLoading}
                            size={'sm'}
                            theme={'outline'}
                            type={'button'}
                            onClick={sendReport}
                          >
                            {t('pages.project.send7DayReport')}
                          </BpButton>
                          <BpButton
                            className={'w-fit'}
                            disabled={!hasSavedRecipients(channel)}
                            loading={sendAlertLoading}
                            size={'sm'}
                            theme={'outline'}
                            type={'button'}
                            onClick={sendAlert}
                          >
                            {t('pages.project.notifyAboutNewAffiliate')}
                          </BpButton>
                          <BpButton
                            className={'w-fit'}
                            disabled={!hasSavedRecipients(channel)}
                            loading={sendTmAlertLoading}
                            size={'sm'}
                            theme={'outline'}
                            type={'button'}
                            onClick={sendTmAlert}
                          >
                            {t('components.alertingSchedule.types.tmShort')}
                          </BpButton>
                        </div>
                      </Field>
                    </div>
                  </div>
                ),
              )}
            </div>
          </div>
        </div>
      </BpCard>
    </form>
  )
}
